import {
  List,
  ListItem,
  Stack,
  Table,
  TableHead,
  Typography,
} from "@mui/material";
import { DiseaseInfo } from "../../commons";
import {
  InterviewTableBody,
  Question,
  QuestionRows,
  SectionTitle,
  TableContainer,
  TableHeaderCell,
  TableQuestionTypography,
  TableRow,
} from "./commons";

export interface InsomniaRiskContent1Props {
  diseaseInfo: DiseaseInfo;
  answeredDate: string;
}

export default function InsomniaRiskContent1({
  diseaseInfo,
  answeredDate,
}: InsomniaRiskContent1Props) {
  return (
    <>
      <Stack sx={{ gap: "16px" }}>
        <SectionTitle text="アテネ不眠尺度の結果" />
        <InsomniaResult diseaseInfo={diseaseInfo} />
      </Stack>
      <Stack sx={{ gap: "16px" }}>
        <Stack direction="row" sx={{ justifyContent: "space-between" }}>
          <SectionTitle text="参考情報：アテネ不眠尺度の回答内容" />
          <Typography sx={{ fontSize: "10px", fontWeight: 400 }}>
            回答日 : {answeredDate}
          </Typography>
        </Stack>
        <InsomniaAnswers diseaseInfo={diseaseInfo} />
      </Stack>
    </>
  );
}

interface InsomniaResultProps {
  diseaseInfo: DiseaseInfo;
}
function InsomniaResult({ diseaseInfo }: InsomniaResultProps) {
  const judgementListItems = [
    ["アテネ不眠尺度0-5点 :", "正常"],
    ["アテネ不眠尺度6-9点 :", "軽症の不眠症が疑われます"],
    ["アテネ不眠尺度10-15点 :", "中等症の不眠症が疑われます"],
    ["アテネ不眠尺度16-24点 :", "重症の不眠症が疑われます"],
    ["---", "回答数不足のための判定不能"],
  ];
  return (
    <Stack direction="row" sx={{ ml: "8px" }}>
      <Stack
        direction="row"
        sx={{
          justifyContent: "center",
          alignItems: "baseline",
          gap: "8px",
          border: "1px solid #000",
          height: "98px",
          width: "238px",
        }}
      >
        {diseaseInfo.score != null ? (
          <>
            <Typography sx={{ fontSize: "40px", fontWeight: 700, mt: "12px" }}>
              {diseaseInfo.score}
            </Typography>
            <Typography sx={{ fontSize: "20px", fontWeight: 700 }}>
              点/ {diseaseInfo.max_score}点
            </Typography>
          </>
        ) : (
          <Typography sx={{ fontSize: "40px", fontWeight: 700, mt: "12px" }}>
            --
          </Typography>
        )}
      </Stack>
      <Stack sx={{ ml: "18px" }}>
        <List sx={{ gap: "10px", p: 0 }}>
          {judgementListItems.map((item, idx) => (
            <ListItem
              key={idx}
              sx={{
                p: 0,
                "& .MuiTypography-root": { fontSize: "10px", fontWeight: 400 },
              }}
            >
              <Typography component="span" sx={{ width: "120px" }}>
                {item[0]}
              </Typography>
              <Typography component="span" sx={{ ml: "4px" }}>
                {item[1]}
              </Typography>
            </ListItem>
          ))}
        </List>
      </Stack>
    </Stack>
  );
}

interface InsomniaAnswersProps {
  diseaseInfo: DiseaseInfo;
}

function InsomniaAnswers({ diseaseInfo }: InsomniaAnswersProps) {
  const questions: Question[] = [
    {
      question: (
        <TableQuestionTypography>
          寝付きの問題について（布団に入って電気を消してから眠るまでに要した時間）
        </TableQuestionTypography>
      ),
      choices: [
        { text: "問題なかった", point: 0 },
        { text: "少し時間がかかった", point: 1 },
        { text: "かなり時間がかかった", point: 2 },
        { text: "非常に時間がかかったか、全く眠れなかった", point: 3 },
      ],
    },
    {
      question: (
        <TableQuestionTypography>
          夜間、睡眠途中に目が覚める問題について
        </TableQuestionTypography>
      ),
      choices: [
        { text: "問題になるほどではなかった", point: 0 },
        { text: "少し困ることがあった", point: 1 },
        { text: "かなり困っている", point: 2 },
        { text: "深刻な状態か、全く眠れなかった", point: 3 },
      ],
    },
    {
      question: (
        <TableQuestionTypography>
          希望する起床時間より早く目覚め、それ以上眠れない問題について
        </TableQuestionTypography>
      ),
      choices: [
        { text: "そのようなことはなかった", point: 0 },
        { text: "少し早かった", point: 1 },
        { text: "かなり早かった", point: 2 },
        { text: "非常に早かった、あるいは全く眠れなかった", point: 3 },
      ],
    },
    {
      question: (
        <TableQuestionTypography>総睡眠時間について</TableQuestionTypography>
      ),
      choices: [
        { text: "十分だった", point: 0 },
        { text: "少し足りなかった", point: 1 },
        { text: "かなり足りなかった", point: 2 },
        { text: "全く足りないか、全く眠れなかった", point: 3 },
      ],
    },
    {
      question: (
        <TableQuestionTypography>
          全体的な睡眠の質について
        </TableQuestionTypography>
      ),
      choices: [
        { text: "満足している", point: 0 },
        { text: "少し不満", point: 1 },
        { text: "かなり不満", point: 2 },
        { text: "非常に不満か、全く眠れなかった", point: 3 },
      ],
    },
    {
      question: (
        <TableQuestionTypography>日中の満足感について</TableQuestionTypography>
      ),
      choices: [
        { text: "いつも通り", point: 0 },
        { text: "少し低下", point: 1 },
        { text: "かなり低下", point: 2 },
        { text: "非常に低下", point: 3 },
      ],
    },
    {
      question: (
        <TableQuestionTypography>
          日中の活動について（身体的および精神的）
        </TableQuestionTypography>
      ),
      choices: [
        { text: "いつも通り", point: 0 },
        { text: "少し低下", point: 1 },
        { text: "かなり低下", point: 2 },
        { text: "非常に低下", point: 3 },
      ],
    },
    {
      question: (
        <TableQuestionTypography>日中の眠気について</TableQuestionTypography>
      ),
      choices: [
        { text: "全くない", point: 0 },
        { text: "少しある", point: 1 },
        { text: "かなりある", point: 2 },
        { text: "激しい", point: 3 },
      ],
    },
  ];
  const cellBorderStyle = {
    border: "solid #000",
    borderWidth: "0 1px 1px 0",
  };
  const headerCellHeight = "34px";
  return (
    <Stack>
      <TableContainer
        sx={{
          ml: "8px",
          borderRadius: 0,
          border: "solid #000",
          borderWidth: "1px 0 0 1px",
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={cellBorderStyle}>
              <TableHeaderCell
                height={headerCellHeight}
                width="306px"
                isFirstCell={true}
              >
                質問
              </TableHeaderCell>
              <TableHeaderCell height={headerCellHeight} width="55px">
                回答
              </TableHeaderCell>
              <TableHeaderCell height={headerCellHeight} width="318px">
                選択肢
              </TableHeaderCell>
              <TableHeaderCell height={headerCellHeight} width="50px">
                点数
              </TableHeaderCell>
            </TableRow>
          </TableHead>
          <InterviewTableBody>
            {questions.map((q, i) => (
              <QuestionRows
                key={i}
                question={q}
                rowIndex={i}
                choiceIndex={diseaseInfo.choice_indices[i]}
                rowHeight="17px"
                choiceStyle={{
                  color: "#000",
                  fontFeatureSettings: "'clig' off, 'liga' off",
                  fontFamily: "Noto Sans JP",
                  fontSize: "8px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "150%",
                  letterSpacing: "0.2px",
                }}
                pointStyle={{
                  color: "#000",
                  fontFeatureSettings: "'clig' off, 'liga' off",
                  fontFamily: "Noto Sans JP",
                  fontSize: "8px",
                  fontStyle: "normal",
                  fontWeight: 400,
                  lineHeight: "150%",
                  letterSpacing: "0.2px",
                }}
              />
            ))}
          </InterviewTableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" sx={{ m: "7px 0 0 8px" }}>
        {diseaseInfo.basis_annotations.map((annotation, i) => (
          <Typography
            key={i}
            sx={{ fontSize: "8px", fontWeight: 400, color: "#707070" }}
          >
            判定の根拠：{annotation}
          </Typography>
        ))}
      </Stack>
    </Stack>
  );
}
